// 장판(바닥 지속 영역): 바닥에 눕은 원 또는 부채꼴이 life초 동안 유지된다. 안쪽은 천천히 도는 옅은 노이즈와 안으로 퍼지는 물결,
// 테두리(원둘레·부채꼴 옆선)는 숨 쉬듯 맥동한다. 처음 0.12초에 가운데서 바깥으로 펼쳐지고 끝나기 전 20%에 사라진다.
// 쓰는 칸: variant 0 = 원 / 1 = 부채꼴(sweep = 전체 각 rad, yaw 방향이 가운데), length = 반경(m), ahead = 중심을 앞으로 옮기는 거리,
//          y = 바닥 높이, life = 유지 시간, color(바닥 빛)·color2(테두리)·intensity·seed. 드로우콜 1.
// 바닥이 평면이라 사각 한 장을 바닥 위 4.5cm에 올린다(깊이 검사 켬: 벽·기둥 뒤에 가려진다).
import * as THREE from 'three/webgpu';
import { abs, atan, clamp, float, length as vlength, mix, sin, smoothstep, step, texture, uv, vec2 } from 'three/tsl';
import { BatchModule, fxAttributes, type Slot } from './batch';
import { quadLayout } from './layout';
import { fxMaterial } from './material';
import { clamp01 } from './path';
import { MODULE_CAPS, type ModuleKind } from './types';

const LIFT = 0.045;
/** 부채꼴 전체 각 하한(rad) */
const FAN_MIN = 0.2;

function makeMaterial(noise: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const env = ctl.y, seed = ctl.z, halfAngle = ctl.w;
  const t = t2.w;
  const p = vec2(uv().x.mul(2).sub(1), uv().y.mul(2).sub(1));
  const r = vlength(p);
  const isFan = step(0.001, halfAngle);
  // 앞(+v)을 기준으로 한 각
  const ang = abs(atan(p.x, p.y));
  const angMask = mix(float(1), float(1).sub(smoothstep(halfAngle.sub(0.05), halfAngle, ang)), isFan);
  // 펼쳐지기: 가운데에서 바깥으로 0.12초
  const grow = clamp(t.mul(8.5), 0, 1);
  const reveal = float(1).sub(smoothstep(grow.mul(1.1).sub(0.12), grow.mul(1.1), r));
  const inside = float(1).sub(smoothstep(0.93, 1.0, r)).mul(angMask).mul(reveal);
  // 테두리: 원둘레 + 부채꼴 옆선
  const rimR = smoothstep(0.8, 0.94, r).mul(float(1).sub(smoothstep(0.94, 1.0, r)));
  const edgeLine = float(1).sub(smoothstep(0.0, 0.05, halfAngle.sub(ang).max(0))).mul(isFan).mul(smoothstep(0.08, 0.2, r));
  const pulse = sin(t.mul(9).add(seed.mul(6.283))).mul(0.3).add(0.7);
  const rim = clamp(rimR.add(edgeLine.mul(0.8)), 0, 1).mul(pulse);
  // 안쪽: 느리게 도는 노이즈 + 안으로 모이는 물결
  const n = texture(noise, p.mul(0.8).add(vec2(t.mul(0.12), t.mul(-0.09).add(seed)))).r;
  const ripple = sin(r.mul(16).add(t.mul(5))).max(0).pow(6).mul(0.3);
  const fill = n.mul(0.28).add(0.12).add(ripple).mul(float(0.4).add(r.mul(0.6)));
  const alpha = clamp(fill.add(rim.mul(1.1)), 0, 1).mul(inside).mul(env);
  const col = mix(t1.rgb, t2.rgb, clamp(rim.mul(0.85).add(n.mul(0.15)), 0, 1)).mul(t1.w).mul(float(0.45).add(rim.mul(0.55)));
  return fxMaterial(col, alpha);
}

export class ZoneModule extends BatchModule {
  readonly kind: ModuleKind = 'zone';

  constructor(noise: THREE.Texture, cap = MODULE_CAPS.zone) {
    super(cap, quadLayout(), makeMaterial(noise), 6, 'fx-zone');
  }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const sin = Math.sin(s.yaw), cos = Math.cos(s.yaw);
    const rx = cos, rz = -sin;
    const L = Math.max(0.1, s.length * s.scale);
    const bx = s.x + sin * s.ahead, bz = s.z + cos * s.ahead, y = s.y + LIFT;
    // 중심 기준 [-L, L]² (근-좌, 근-우, 원-좌, 원-우)
    out[o] = bx - sin * L - rx * L; out[o + 1] = y; out[o + 2] = bz - cos * L - rz * L;
    out[o + 3] = bx - sin * L + rx * L; out[o + 4] = y; out[o + 5] = bz - cos * L + rz * L;
    out[o + 6] = bx + sin * L - rx * L; out[o + 7] = y; out[o + 8] = bz + cos * L - rz * L;
    out[o + 9] = bx + sin * L + rx * L; out[o + 10] = y; out[o + 11] = bz + cos * L + rz * L;
  }

  protected ctl(s: Slot, out: Float32Array): void {
    const age01 = clamp01(s.age / s.life);
    out[0] = age01;
    out[1] = Math.min(1, s.age / 0.05) * (1 - clamp01((age01 - 0.8) / 0.2));
    out[2] = s.seed;
    // 셰이더에는 부채꼴 반각만 보낸다(0 = 원)
    out[3] = s.variant >= 0.5 ? Math.min(Math.PI * 2, Math.max(FAN_MIN, Math.abs(s.sweep))) * 0.5 : 0;
  }

  protected override tint2w(s: Slot): number { return s.age; }
}
